// Backend-API Auth Helper (relative URLs)
// localStorage-basierte Authentication wie im Laravel-System

export class BackendAuth {
  // CSRF Token Management
  static _csrfToken = null;
  static _csrfExpiry = null;

  /**
   * Get current CSRF token (from memory or localStorage)
   */
  static getCsrfToken() {
    if (typeof window === 'undefined') return null;

    // Check memory cache first
    if (this._csrfToken && this._csrfExpiry && new Date(this._csrfExpiry) > new Date()) {
      return this._csrfToken;
    }

    // Try localStorage
    const stored = localStorage.getItem('csrf_token');
    const expiry = localStorage.getItem('csrf_expiry');
    if (stored && expiry && new Date(expiry) > new Date()) {
      this._csrfToken = stored;
      this._csrfExpiry = expiry;
      return stored;
    }

    return null;
  }

  /**
   * Store CSRF token in memory and localStorage
   */
  static setCsrfToken(token, expiresAt) {
    if (typeof window === 'undefined') return;

    this._csrfToken = token;
    this._csrfExpiry = expiresAt;
    localStorage.setItem('csrf_token', token);
    localStorage.setItem('csrf_expiry', expiresAt);
  }

  /**
   * Clear CSRF token
   */
  static clearCsrfToken() {
    this._csrfToken = null;
    this._csrfExpiry = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('csrf_token');
      localStorage.removeItem('csrf_expiry');
    }
  }

  /**
   * Wipe ALL user-bound state from localStorage + sessionStorage on auth changes
   * (logout, defensive on login). Keeps only global UI prefs (theme, language).
   *
   * Why: parent and module-iframes share the same react.xoffi.com origin →
   * module-prefixed cache keys (tm_token, pp_*, task-manager.*, ...) survive
   * BackendAuth.logout()'s targeted removes and leak previous user's data
   * into the next session. Pattern-based whitelist is safer than blacklist.
   */
  static _clearUserBoundState() {
    if (typeof window === 'undefined') return;
    const KEEP = new Set(['theme', 'language']);
    // Device-level preferences that must survive the wipe. No tenant data in them.
    //  - xoffi_passkey_dismissed_<userId>: "Nicht wieder anzeigen" of the passkey
    //    setup prompt. login() runs this wipe BEFORE SigninClient checks the flag,
    //    so without this entry the prompt came back on every sign-in (28.09.2026).
    const KEEP_PREFIXES = ['xoffi_passkey_dismissed_'];
    try {
      const drop = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && !KEEP.has(k) && !KEEP_PREFIXES.some(p => k.startsWith(p))) drop.push(k);
      }
      drop.forEach(k => { try { localStorage.removeItem(k); } catch {} });
    } catch {}
    try { sessionStorage.clear(); } catch {}
    this._wipeOfflineCaches();
  }

  /**
   * Tenant-isolation on auth change: drop the offline data that could leak a
   * previous user's tenant data into the next session on a shared device.
   *  - IndexedDB persisted TanStack Query cache (clearPersistedQueryCache)
   *  - the service worker's page/RSC caches (WIPE_DATA_CACHES message)
   * Static assets (JS/CSS chunks) are public and intentionally kept. Async and
   * fire-and-forget — logout must never block on this. Dynamic import keeps this
   * client-only module out of any SSR/edge import graph of BackendAuth.
   */
  static _wipeOfflineCaches() {
    if (typeof window === 'undefined') return;
    try {
      navigator.serviceWorker?.controller?.postMessage({ type: 'WIPE_DATA_CACHES' });
    } catch {}
    import('@/lib/query-persist')
      .then((m) => m.clearPersistedQueryCache())
      .catch(() => {});
  }

  /**
   * Fetch a fresh CSRF token from the backend
   * Call this on app initialization
   */
  static async fetchCsrfToken() {
    try {
      const response = await fetch('/api/csrf-token', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.csrf_token) {
          this.setCsrfToken(data.csrf_token, data.expires_at);
          console.log('🔐 CSRF token fetched successfully');
          return data.csrf_token;
        }
      }

      console.warn('⚠️ Failed to fetch CSRF token');
      return null;
    } catch (error) {
      console.error('❌ Error fetching CSRF token:', error);
      return null;
    }
  }

  /**
   * Ensure we have a valid CSRF token, fetching if needed
   */
  static async ensureCsrfToken() {
    let token = this.getCsrfToken();
    if (!token) {
      token = await this.fetchCsrfToken();
    }
    return token;
  }

  /**
   * Refresh CSRF token if it's older than maxAge (in ms)
   * Called periodically from health checks to keep token fresh
   */
  static async refreshCsrfTokenIfOld(maxAgeMs = 30 * 60 * 1000) {
    if (typeof window === 'undefined') return;

    const expiry = this._csrfExpiry || localStorage.getItem('csrf_expiry');
    if (!expiry) {
      // No token - fetch new one
      await this.fetchCsrfToken();
      return;
    }

    // Check how much time is left until expiry
    const expiryDate = new Date(expiry);
    const now = new Date();
    const timeLeft = expiryDate.getTime() - now.getTime();

    // Token expires in 4 hours (14400000ms), refresh if less than 3.5h left
    // Or use custom maxAge
    if (timeLeft < maxAgeMs) {
      console.log('🔐 CSRF token getting old, refreshing...');
      await this.fetchCsrfToken();
    }
  }

  static getToken() {
    if (typeof window === 'undefined') return null;

    // Try to get token from multiple sources (for compatibility)
    let token = localStorage.getItem('auth_token') ||
                localStorage.getItem('token') ||
                this.getCookie('auth-token') ||
                this.getCookie('token');

    // 🍪 Sync localStorage token to cookie for EventSource/SSE requests
    if (token && !this.getCookie('auth-token')) {
      this.syncTokenToCookie(token);
    }

    return token;
  }

  /**
   * Sync token to cookie for browser APIs that can't set custom headers
   * (EventSource, img tags, etc.)
   * Cookie domain is set to allow subdomain access (e.g., project-id.react.xoffi.com)
   */
  static syncTokenToCookie(token) {
    if (typeof window === 'undefined' || !token) return;

    const expires = new Date();
    expires.setTime(expires.getTime() + (24 * 60 * 60 * 1000)); // 24 hours

    // Extract root domain to allow subdomain access (e.g., .xoffi.com or .react.xoffi.com)
    const domain = this.getCookieDomain();
    const domainAttr = domain ? `; domain=${domain}` : '';

    // Use dynamic SameSite based on environment (HTTPS: None+Secure, HTTP: Lax)
    const sameSite = this.getCookieSameSite();
    document.cookie = `auth-token=${token}; expires=${expires.toUTCString()}; path=/${domainAttr}; ${sameSite}`;
  }

  /**
   * Get cookie domain for subdomain sharing
   * Returns domain with leading dot (e.g., .xoffi.com) to share cookies with subdomains
   */
  static getCookieDomain() {
    if (typeof window === 'undefined') return null;

    const host = window.location.host.split(':')[0]; // Remove port

    // For localhost, don't set domain (cookies work without it)
    if (host === 'localhost' || host === '127.0.0.1') {
      return null;
    }

    // For production domains like react.xoffi.com, use .xoffi.com
    // This allows cookies to work on subdomains like test-page-61.react.xoffi.com
    const parts = host.split('.');
    if (parts.length >= 2) {
      // Use the top-level domain + TLD (e.g., .xoffi.com)
      return '.' + parts.slice(-2).join('.');
    }

    return null;
  }

  /**
   * Get cookie SameSite attribute based on environment
   * Production (HTTPS): SameSite=None; Secure - required for cross-site iframe
   * Localhost (HTTP): SameSite=Lax - more compatible for development
   */
  static getCookieSameSite() {
    if (typeof window === 'undefined') return 'SameSite=Lax';

    const isSecure = window.location.protocol === 'https:';
    // SameSite=None requires Secure flag, which requires HTTPS
    return isSecure ? 'SameSite=None; Secure' : 'SameSite=Lax';
  }

  static getCookie(name) {
    if (typeof window === 'undefined') return null;
    
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return null;
  }

  static getUser() {
    if (typeof window === 'undefined') return null;
    const userData = localStorage.getItem('user_data');
    const user = userData ? JSON.parse(userData) : null;

    return user;
  }

  /**
   * Pull authoritative user state from the backend (/api/auth/health) and merge
   * it into localStorage.user_data. The DB is the source of truth — this keeps
   * fields like `org_onboarding_completed` from going stale in the browser.
   *
   * Merge-only: never drops existing fields, only overrides the ones the health
   * endpoint reports. Returns the synced/merged user object (or current user on
   * any failure — never throws, never logs out).
   */
  static async syncUserDataFromHealth() {
    if (typeof window === 'undefined') return null;
    try {
      const response = await this.apiCall('/api/auth/health');
      // 401 (auto-logout) returns null; 503 (offline) returns a non-ok response.
      if (!response || !response.ok) return this.getUser();

      const healthData = await response.json();
      const user = this.getUser() || {};

      // Merge any user-bound fields the health endpoint surfaces. We explicitly
      // sync org_onboarding_completed (the field driving the onboarding guard);
      // other top-level scalar fields are merged opportunistically without
      // clobbering nested/structural user data.
      let changed = false;
      if (healthData && healthData.org_onboarding_completed !== undefined &&
          user.org_onboarding_completed !== healthData.org_onboarding_completed) {
        user.org_onboarding_completed = healthData.org_onboarding_completed;
        changed = true;
      }

      if (changed) {
        localStorage.setItem('user_data', JSON.stringify(user));
      }
      return user;
    } catch {
      // Network/parse error — keep whatever we have, do not disrupt the session.
      return this.getUser();
    }
  }

  static getUserAvatarUrl() {
    const user = this.getUser();
    
    
    if (!user) return '/media/avatars/blank.png';
    
    // Wenn user.avatarUrl existiert und nicht leer ist
    if (user.avatarUrl && user.avatarUrl.trim()) {
      
      return user.avatarUrl;
    }
    
    // Fallback wie im Laravel-System
    
    return '/media/avatars/blank.png';
  }

  static isAuthenticated() {
    return !!this.getToken() && !!this.getUser();
  }

  static logout(tokenOverride = null) {
    if (typeof window === 'undefined') return;

    // Invalidate token on backend (fire-and-forget — don't block logout).
    // tokenOverride: the token that actually failed (401 path) — never read a
    // possibly newer token from storage and blacklist it by accident.
    const token = tokenOverride || this.getToken();
    if (token) {
      try {
        fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          keepalive: true, // Ensure request completes even if page navigates
        }).catch(() => {}); // Ignore errors — client-side cleanup always proceeds
      } catch {
        // Ignore — logout must always succeed client-side
      }
    }

    // In-memory CSRF cache reset (localStorage handled by _clearUserBoundState below)
    this._csrfToken = null;
    this._csrfExpiry = null;

    // Wipe ALL user-bound state (auth_token, user_data, domain_data, laravel_*,
    // csrf_*, xoffi_domain_cache_*, all module-prefixed keys like tm_*, pp_*,
    // task-manager.*, plus full sessionStorage). Keeps only theme + language.
    this._clearUserBoundState();

    // Clear JWT token cookie (with domain for subdomain sharing)
    const domain = this.getCookieDomain();
    const domainAttr = domain ? `; domain=${domain}` : '';
    const sameSite = this.getCookieSameSite();
    document.cookie = `auth-token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainAttr}; ${sameSite}`;
  }

  /**
   * Rueckkehr-Pfad fuer den Login: Pfad + Query-String (z.B. ?preview=1&invoice_date=...),
   * damit nach dem Login dieselbe Aktion ausgefuehrt wird. Ohne Query konnte ein
   * Login auf eine andere Aktion fuehren (Vorfall 13.10.2026: Vorschau -> echte Rechnung).
   * Ein verschachtelter `return`-Parameter wird entfernt (verhindert Schleifen),
   * /signin und /signup fuehren immer nach /dashboard. Nur same-origin Pfade.
   */
  static buildReturnPath(returnUrl = null) {
    if (typeof window === 'undefined') return '/dashboard';

    let pathname = window.location.pathname;
    let search = window.location.search || '';

    if (returnUrl) {
      try {
        const u = new URL(returnUrl, window.location.origin);
        if (u.origin === window.location.origin) {
          pathname = u.pathname;
          search = u.search;
        }
      } catch { /* ungueltig -> aktuelle Seite verwenden */ }
    }

    if (!pathname || !pathname.startsWith('/') || pathname.startsWith('//')
        || pathname.startsWith('/signin') || pathname.startsWith('/signup')) {
      return '/dashboard';
    }

    try {
      const params = new URLSearchParams(search);
      params.delete('return');
      const qs = params.toString();
      return qs ? `${pathname}?${qs}` : pathname;
    } catch {
      return pathname;
    }
  }

  static redirectToLogin(returnUrl = null) {
    if (typeof window === 'undefined') return;
    const safePath = this.buildReturnPath(returnUrl);
    const loginUrl = `/signin?return=${encodeURIComponent(safePath)}`;
    window.location.href = loginUrl;
  }

  static setLaravelToken(token, expiresAt, serverUrl) {
    if (typeof window === 'undefined') return;
    
    localStorage.setItem('laravel_token', token);
    localStorage.setItem('laravel_expires_at', expiresAt);
    localStorage.setItem('laravel_server_url', serverUrl);
    
    
  }

  static getLaravelToken() {
    if (typeof window === 'undefined') return null;
    
    const token = localStorage.getItem('laravel_token');
    const expiresAt = localStorage.getItem('laravel_expires_at');
    
    if (!token || !expiresAt) {
      return null;
    }
    
    // Check if token is expired
    if (new Date() > new Date(expiresAt)) {
      
      localStorage.removeItem('laravel_token');
      localStorage.removeItem('laravel_expires_at');
      localStorage.removeItem('laravel_server_url');
      return null;
    }
    
    return {
      token,
      expiresAt,
      serverUrl: localStorage.getItem('laravel_server_url')
    };
  }

  static async login(credentials) {
    // account_selection: dieser Client kann die Konto-Auswahl (select_organisation)
    // anzeigen. Ohne das Flag bleibt der Server beim bisherigen Verhalten.
    return this._postLogin('/api/auth/login', { ...credentials, account_selection: true });
  }

  /**
   * Zweiter Schritt bei mehrfach vergebener E-Mail-Adresse: der Nutzer hat aus
   * der Liste (select_organisation) ein Konto gewaehlt. Antwort wie /login
   * (inkl. mfa_required), Tokens werden identisch gespeichert.
   */
  static async loginSelect(selectionToken, key) {
    return this._postLogin('/api/auth/login/select', { selection_token: selectionToken, key });
  }

  static async _postLogin(url, payload) {
    try {
      // Use RELATIVE URL - let webserver route to backend!
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        // Preserve status + structured payload so callers can branch on
        // special cases (e.g. 403 { detail: { requires_verification, email } }
        // for unverified email signups). FastAPI nests its error under `detail`.
        const message =
          (data?.detail && typeof data.detail === 'object' && data.detail.message) ||
          (typeof data?.detail === 'string' && data.detail) ||
          data?.message ||
          'Login failed';
        const error = new Error(message);
        error.status = response.status;
        error.payload = data;
        throw error;
      }

      // Mehrere Konten mit dieser Adresse: Auswahl anzeigen, NICHTS speichern.
      if (data.select_organisation && data.selection_token) {
        return data;
      }

      // MFA: es gibt noch kein Token (nur temp_token) — nichts speichern,
      // der Aufrufer zeigt den MFA-Dialog.
      if (data.success && data.mfa_required) {
        return data;
      }

      if (data.success) {
        // Defensive: wipe any stale state from a previous session BEFORE writing
        // new tokens. Covers "browser closed without logout → different user logs
        // in" — without this, module-iframe caches (tm_token, pp_*, ...) on the
        // shared react.xoffi.com origin would leak the previous user's data.
        this._clearUserBoundState();

        // Laravel-style tokens im localStorage speichern
        localStorage.setItem('auth_token', data.data.token);
        localStorage.setItem('user_data', JSON.stringify(data.data.user));
        localStorage.setItem('domain_data', JSON.stringify(data.data.domain));
        
        // 🍪 Set JWT token as cookie for <img>, <script>, subdomain iframes, etc.
        const expires = new Date();
        expires.setTime(expires.getTime() + (24 * 60 * 60 * 1000)); // 24 hours
        const domain = this.getCookieDomain();
        const domainAttr = domain ? `; domain=${domain}` : '';
        const sameSite = this.getCookieSameSite();
        document.cookie = `auth-token=${data.data.token}; expires=${expires.toUTCString()}; path=/${domainAttr}; ${sameSite}`;
        
        
        // Laravel-Token speichern falls verfügbar
        if (data.data.laravel_token) {
          
          
          this.setLaravelToken(
            data.data.laravel_token,
            data.data.laravel_expires_at,
            data.data.laravel_server_url
          );
          
        } 
        
        return data;
      } else {
        // Preserve the structured body so callers can branch on special cases
        // (error_code-basierte Contracts im success=false-Body).
        const error = new Error(data.message || 'Login failed');
        error.payload = data;
        throw error;
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  static async refreshToken() {
    try {
      // Use RELATIVE URL
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getToken()}`,
        },
      });

      const data = await response.json();

      if (response.ok && data.success) {
        localStorage.setItem('auth_token', data.data.token);

        // 🍪 Also update cookie for SSE/EventSource and subdomain access
        const expires = new Date();
        expires.setTime(expires.getTime() + (24 * 60 * 60 * 1000)); // 24 hours
        const domain = this.getCookieDomain();
        const domainAttr = domain ? `; domain=${domain}` : '';
        const sameSite = this.getCookieSameSite();
        document.cookie = `auth-token=${data.data.token}; expires=${expires.toUTCString()}; path=/${domainAttr}; ${sameSite}`;

        // 🔄 Keep user_data fresh after refresh.
        // /api/auth/refresh only returns a new token, NOT the user object — so a
        // stale user_data (e.g. org_onboarding_completed:false from before the
        // onboarding submit) would survive and cause the protected-layout guard
        // to redirect-loop back to /admin/company/onboarding. Merge the latest
        // user fields from /api/auth/health into user_data (merge only, never drop).
        try {
          if (data.data.user && typeof data.data.user === 'object') {
            // If refresh ever starts returning the user object, prefer it.
            const existing = this.getUser() || {};
            localStorage.setItem('user_data', JSON.stringify({ ...existing, ...data.data.user }));
          } else {
            await this.syncUserDataFromHealth();
          }
        } catch { /* non-fatal — token already refreshed */ }

        return data;
      } else {
        throw new Error(data.message || 'Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      this.logout();
      throw error;
    }
  }

  static async apiCall(endpoint, options = {}) {
    const token = this.getToken();
    const user = this.getUser();

    // Use RELATIVE URLs for API calls
    const url = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    // Determine HTTP method (default GET)
    const method = (options.method || 'GET').toUpperCase();

    // Build enhanced headers with user context
    const defaultHeaders = {
      'Accept': 'application/json',
      'User-Agent': 'Frontend-API-Client',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };

    // Add CSRF token for state-changing requests (POST, PUT, DELETE, PATCH)
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      // Use ensureCsrfToken to fetch token if not available
      const csrfToken = await this.ensureCsrfToken();
      if (csrfToken) {
        defaultHeaders['X-CSRF-Token'] = csrfToken;
      }
    }

    // Only set Content-Type for non-FormData requests
    // FormData automatically sets correct Content-Type with boundary
    if (!(options.body instanceof FormData)) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    // Add user context headers if available
    if (user) {
      if (user.id) defaultHeaders['X-User-Id'] = String(user.id);
      // Robust organisation/tenant resolution: support multiple common fields
      try {
        const orgKeys = [
          'organisation', 'organization', 'organisation_id', 'organization_id',
          'org_id', 'org', 'tenant_id', 'tenant'
        ];
        let orgValue = null;
        for (const key of orgKeys) {
          const v = user?.[key];
          if (v !== undefined && v !== null && String(v).trim() !== '') {
            orgValue = v;
            break;
          }
        }
        if (orgValue !== null) {
          defaultHeaders['X-Organisation'] = String(orgValue);
        }
      } catch {}
      if (user.menu_group_id) defaultHeaders['X-Menu-Group'] = String(user.menu_group_id);
      if (user.username) defaultHeaders['X-Username'] = user.username;
    }

    // Add host context headers
    if (typeof window !== 'undefined') {
      const host = window.location.host;
      const domain = host.split(':')[0];
      defaultHeaders['X-Original-Host'] = domain;
      defaultHeaders['X-Forwarded-Host'] = host;

      // Nexus access is verified via nexus_core_access JWT claim, no master password header needed
    }

    const defaultOptions = {
      method: 'GET', // Default method
      headers: defaultHeaders,
    };

    const mergedOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...(options.headers || {}),
      },
    };

    // Serialize body to JSON if it's an object and not FormData
    if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
      mergedOptions.body = JSON.stringify(options.body);
    }

    try {
      const response = await fetch(url, mergedOptions);

      // 🚨 Detect cross-origin redirect (browser-cached 301 or server-induced)
      // This prevents silent API calls going to the wrong server (e.g. app.xoffi.ai instead of react.xoffi.com)
      if (typeof window !== 'undefined' && response.redirected && response.url) {
        const responseOrigin = new URL(response.url).origin;
        if (responseOrigin !== window.location.origin) {
          console.error(`[BackendAuth] API call redirected to different origin: ${response.url} (expected ${window.location.origin})`);
          console.error('[BackendAuth] This is likely a browser-cached 301 redirect. Clear site data in DevTools → Application → Storage.');
          throw new Error(`API call was redirected to ${responseOrigin} — clear browser cache for ${window.location.origin}`);
        }
      }

      // 🔌 503 Service Unavailable - Server offline/unreachable
      // This is NOT an auth error - just show notification, no redirect!
      if (response.status === 503) {
        try {
          await response.clone().json(); // Parse to validate JSON

          // Dispatch custom event for global toast notification
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('backend-error', {
              detail: {
                type: 'service-unavailable',
                status: 503,
                message: 'Server ist momentan nicht erreichbar. Bitte versuchen Sie es später erneut.',
                timestamp: new Date().toISOString()
              }
            }));
          }
        } catch {
          // Dispatch event even if parsing fails
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('backend-error', {
              detail: {
                type: 'service-unavailable',
                status: 503,
                message: 'Server ist momentan nicht erreichbar',
                timestamp: new Date().toISOString()
              }
            }));
          }
        }

        // Return response as-is - let caller handle the error
        // NO logout, NO redirect - just server offline
        return response;
      }

      // 🚨 CRITICAL: Handle 401 Unauthorized - Check if it's a real auth error
      if (response.status === 401) {
        console.error('🚫 [BackendAuth] 401 Unauthorized detected');

        // Race guard: if the token that made THIS request is no longer the
        // current one, a newer session was stored while the request was in
        // flight (e.g. OAuth/eID callback) — a stale 401 must neither log it
        // out nor blacklist the fresh token.
        if (this.getToken() !== token) {
          console.warn('🚫 [BackendAuth] 401 from a stale token - keeping the newer session');
          return null;
        }

        // Try to read error details to determine if it's a REAL auth error
        try {
          const errorData = await response.clone().json();
          console.error('🚫 [BackendAuth] 401 Error details:', errorData);

          // Check if backend explicitly requests logout (real auth error)
          if (errorData?.proxy_info?.action_required === 'logout' ||
              errorData?.error_code === 'UNAUTHORIZED') {
            this.logout(token);
            this.redirectToLogin();
            return null; // Return null to prevent further processing
          }

          // If action_required is 'none', it's NOT an auth error (e.g., connection error)
          if (errorData?.proxy_info?.action_required === 'none') {
            return response; // Return as-is, no logout
          }
        } catch {
          // If error is not JSON, just proceed with logout as fallback
          console.error('🚫 [BackendAuth] Could not parse 401 error response - assuming auth error');
        }

        // Default behavior for 401: logout and redirect (safety fallback)
        this.logout(token);
        this.redirectToLogin();
        return null;
      }

      // 🔐 403 CSRF Token Error - Try to refresh token and retry once
      if (response.status === 403 && !options._csrfRetry) {
        try {
          const errorText = await response.clone().text();
          // Check if it's a CSRF token error
          if (errorText && errorText.toLowerCase().includes('csrf')) {
            console.warn('🔐 [BackendAuth] CSRF token invalid/expired - fetching new token...');

            // Clear old token and fetch new one
            this.clearCsrfToken();
            const newToken = await this.fetchCsrfToken();

            if (newToken) {
              console.log('🔐 [BackendAuth] Got new CSRF token - retrying request...');
              // Retry the request with new token (mark as retry to prevent infinite loop)
              return this.apiCall(endpoint, { ...options, _csrfRetry: true });
            }
          }
        } catch (e) {
          console.error('🔐 [BackendAuth] Error handling CSRF refresh:', e);
        }
      }

      return response;
    } catch (error) {
      // Gewollter Abbruch (AbortController, z.B. ueberholter Request) ist kein
      // Fehler — nicht loggen, sonst zeigt das Next-Dev-Overlay "AbortError".
      if (error?.name !== 'AbortError' && !options?.signal?.aborted) {
        console.error('❌ [BackendAuth] API call error:', error);
      }
      throw error;
    }
  }

  /**
   * Convenience method for JSON API calls
   */
  static async apiCallJson(endpoint, options = {}) {
    const response = await this.apiCall(endpoint, options);

    // 🚨 CRITICAL: Handle null response (401 auto-logout case)
    if (!response) {
      return null; // Return null gracefully - redirect already happened
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ [BackendAuth] API error ${response.status}:`, errorText);

      let parsedPayload;
      try {
        parsedPayload = errorText ? JSON.parse(errorText) : null;
      } catch {
        parsedPayload = null;
      }

      // 🚨 429 Quota-Exceeded — globaler Toast + Custom-Event für Banner
      if (response.status === 429 && parsedPayload?.error === 'quota_exceeded') {
        try {
          if (typeof window !== 'undefined') {
            // Custom-Event damit Banner-Komponenten reagieren können
            window.dispatchEvent(
              new CustomEvent('xoffi:ai-quota-exceeded', { detail: parsedPayload })
            );
            // Toast über sonner (lazy, falls verfügbar)
            try {
              const { toast } = await import('sonner');
              toast.error('AI-Limit erreicht', {
                description: `${(parsedPayload.used || 0).toLocaleString('de-DE')} / ${(parsedPayload.limit || 0).toLocaleString('de-DE')} Tokens. Plan upgraden für mehr.`,
                action: parsedPayload.upgrade_url
                  ? {
                      label: 'Upgrade',
                      onClick: () => {
                        window.location.href = parsedPayload.upgrade_url;
                      },
                    }
                  : undefined,
                duration: 8000,
              });
            } catch {
              /* sonner nicht verfügbar */
            }
          }
        } catch {
          /* nichts blockierendes */
        }
      }

      let detailMessage = `API error ${response.status}`;
      if (parsedPayload) {
        if (typeof parsedPayload.detail === 'string' && parsedPayload.detail.trim()) {
          detailMessage = parsedPayload.detail.trim();
        } else if (parsedPayload.message) {
          detailMessage = String(parsedPayload.message);
        }
      } else if (errorText?.trim()) {
        detailMessage = errorText.trim();
      }

      const error = new Error(detailMessage);
      error.status = response.status;
      error.payload = parsedPayload;
      error.originalMessage = errorText;
      throw error;
    }

    return await response.json();
  }

  /**
   * Convenience method for POST requests with JSON body
   */
  static async apiPost(endpoint, data = {}) {
    return this.apiCall(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Convenience method for PUT requests with JSON body
   */
  static async apiPut(endpoint, data = {}) {
    return this.apiCall(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * Convenience method for DELETE requests
   */
  static async apiDelete(endpoint) {
    return this.apiCall(endpoint, {
      method: 'DELETE'
    });
  }

  static async getProfile() {
    try {
      const response = await this.apiCall('/api/user/profile');
      return await response.json();
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  }
}
